import { z } from 'zod';

// ─────────────────────────────────────────────
// Schémas Zod des réponses API (ADR 0005 D3 — validation au bord réseau).
//
// Doctrine de placement (ADR 0005 D1) : ces schémas décrivent le FIL — ce
// que l'API renvoie AUJOURD'HUI, forme brute Prisma (français, `statut`,
// `nomPlat`, etc.) — jamais le domaine front mappé (`apps/web/src/lib/
// types.ts`, anglais/français mixte). L'adaptateur mappe fil validé →
// domaine dans `apps/web/src/lib/api.ts` ; aucune logique de mapping ni
// aucune valeur métier ne vit ici — des formes pures, rien d'autre.
//
// Déploiement ordonné par le risque (ADR 0005 D3) : argent & identité
// d'abord (auth, payments, subscriptions, formules), le reste migre
// incrémentalement — pas de big-bang.
//
// Règle d'avant : tout nouvel endpoint ship avec son schéma de réponse ici.
// Un schéma qui rejette du trafic réel casse l'appli entière — en cas de
// doute, `.passthrough()` / champ `.optional()` plutôt que de resserrer ;
// seule exception délibérée : `authUserSchema`, `.strict()` par exigence de
// sécurité (exclure passwordHash par construction).
// ─────────────────────────────────────────────

// ── Primitives réutilisées à travers plusieurs schémas ──
export const formuleSchema = z.enum(['DAY', 'WEEK', 'MONTH']);
export const operateurSchema = z.enum(['MTN', 'MOOV']);

// ── Auth — POST /auth/login, /auth/register, /auth/refresh ──
// Objet `user` STRICT et énuméré à la main (pas de `.passthrough()`) :
// exclut `passwordHash` par construction. Si le backend en venait à
// renvoyer un champ non listé ici (erreur de select — ex. passwordHash),
// la validation échoue au lieu de laisser fuiter silencieusement une
// donnée sensible côté client.
export const authUserSchema = z
  .object({
    id: z.string(),
    nom: z.string(),
    prenom: z.string(),
    email: z.string().nullable(),
    telephone: z.string(),
    profil: z.enum(['STUDENT_EPITECH', 'OTHER']),
    isAdmin: z.boolean(),
    // Renvoyé par /auth/register (select explicite) ; absent de /auth/login.
    createdAt: z.string().optional(),
  })
  .strict();

export const authResponseSchema = z.object({
  user: authUserSchema,
  accessToken: z.string(),
});

// POST /auth/refresh renvoie uniquement { accessToken } (pas de `user`).
export const refreshResponseSchema = z.object({
  accessToken: z.string(),
});

// ── Sessions — GET /auth/sessions, POST /auth/sessions/revoke-all (T7) ──
// `.strict()` par la même exigence de sécurité que `authUserSchema` : exclut
// `refreshHash`/`prevRefreshHash`/`userId` par construction — si le contrôleur
// venait un jour à oublier un `select` explicite et à renvoyer la ligne
// Prisma brute, la validation échoue au lieu de laisser fuiter un hash de
// session côté client. `courante` = id de session === sid du refresh cookie
// présenté à la requête (cf. `sessions.service.ts`, jamais recalculé ici).
export const sessionSchema = z
  .object({
    id: z.string(),
    createdAt: z.string(),
    lastUsedAt: z.string(),
    userAgent: z.string().nullable(),
    courante: z.boolean(),
  })
  .strict();

export const sessionsResponseSchema = z.object({
  sessions: z.array(sessionSchema),
});

// POST /auth/sessions/revoke-all — { revoquees: nombre de sessions révoquées }.
export const revokeAllSessionsResponseSchema = z.object({
  revoquees: z.number(),
});

// ── Menus — GET /menus/today ──
export const menuSchema = z
  .object({
    id: z.string(),
    nomPlat: z.string(),
    description: z.string().nullable().optional(),
    photoUrl: z.string().nullable().optional(),
    dateLivraison: z.string(),
    estActif: z.boolean().optional(),
    publishedAt: z.string().nullable().optional(),
    publishedBy: z.string().nullable().optional(),
    menuCandidateId: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    // `include: { menuCandidate: { select: { totalVotes: true } } }`
    menuCandidate: z.object({ totalVotes: z.number() }).nullable().optional(),
    // Calculé à chaque requête, jamais mis en cache (ADR 0004 D2).
    commande: z
      .object({
        ouverte: z.boolean(),
        deadline: z.string(),
        // Pourquoi c'est fermé (artboard 5d) : la ligne sous le CTA dit la raison, avec la voix.
        raison: z.enum(['DEADLINE', 'WEEKEND']).optional(),
      })
      .optional(),
    // Accroche du drop (docs/design-handoff.md item 1, décision 2026-07-14) :
    // phrase éditable par l'admin à la publication + le mot qu'elle marque
    // en mot-choc (kit design §4). Forme fil réelle (T3) : deux champs
    // texte plats sur `Menu`, tous deux optionnels/nullable — jamais de
    // valeur par défaut, l'API renvoie `null` sans accroche saisie.
    accroche: z.string().nullable().optional(),
    accrocheMot: z.string().nullable().optional(),
  })
  .passthrough();

// ── Formules — GET /formules (catalogue tarifaire, ADR 0001 D6) ──
export const formuleInfoSchema = z
  .object({
    id: formuleSchema,
    credits: z.number(),
    dureeJours: z.number(),
    prixEpitech: z.number(),
    prixAutre: z.number(),
    actif: z.boolean(),
  })
  // Le modèle Prisma renvoie aussi updatedAt/updatedBy — hors contrat front
  // (FormuleInfo) : l'API peut renvoyer plus, jamais moins (D1).
  .passthrough();

// ── Subscriptions — GET /subscriptions/me, POST /subscriptions ──
export const subscriptionSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    formule: formuleSchema,
    creditsTotal: z.number(),
    creditsRestants: z.number(),
    prixPaye: z.number().optional(),
    startedAt: z.string().optional(),
    expiresAt: z.string().nullable().optional(),
    renewedAt: z.string().nullable().optional(),
    delaiRenouvellementJours: z.number().nullable().optional(),
    numeroCycle: z.number().optional(),
    statut: z.enum(['PENDING', 'ACTIVE', 'EXPIRED', 'CANCELLED']),
    remboursementAt: z.string().nullable().optional(),
    remboursementMontant: z.number().nullable().optional(),
    remboursementRaison: z.string().nullable().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();

// ── Payments — GET /payments/me, GET /payments/:id ──
export const paymentSchema = z
  .object({
    id: z.string(),
    userId: z.string().optional(),
    subscriptionId: z.string().optional(),
    montant: z.number().optional(),
    operateur: operateurSchema.optional(),
    referenceExterne: z.string().nullable().optional(),
    statut: z.enum(['PENDING', 'CONFIRMED', 'FAILED', 'EXPIRED']).optional(),
    surPaiement: z.boolean().optional(),
    paidAt: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    // Présent sur /payments/me et /admin/payments (`include`) ; absent sur
    // /payments/:id (lecture brute, sans include).
    subscription: z
      .object({
        formule: formuleSchema.optional(),
        creditsTotal: z.number().optional(),
      })
      .nullable()
      .optional(),
    // Présent uniquement sur /admin/payments (hors périmètre prioritaire ici).
    user: z
      .object({
        nom: z.string().optional(),
        prenom: z.string().optional(),
        email: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

// ── Orders — GET /orders/me ──
// NOTE (dette existante, hors périmètre Task 2) : le fil renvoie `statut` en
// anglais (PENDING/CONFIRMED/DELIVERED/CANCELLED — énum Prisma OrderStatus)
// alors que le type domaine front `Order` (types.ts) déclare `status` en
// français. Ce schéma valide fidèlement le fil réel d'aujourd'hui ; il ne
// corrige pas ce décalage de domaine, qui reste à traiter séparément.
// `quantite` (nombre de repas) est une colonne d'Order depuis la vague finale
// du chantier ambassadeurs.
export const orderSchema = z
  .object({
    id: z.string(),
    userId: z.string().optional(),
    menuId: z.string().optional(),
    subscriptionId: z.string().optional(),
    orderedAt: z.string().optional(),
    delaiApresPublicationMin: z.number().nullable().optional(),
    statut: z.enum(['PENDING', 'CONFIRMED', 'DELIVERED', 'CANCELLED']),
    signalement: z.boolean().optional(),
    signalementDetail: z.string().nullable().optional(),
    livreeAt: z.string().nullable().optional(),
    quantite: z.number().int().optional(),
    createdAt: z.string().optional(),
    menu: z
      .object({
        nomPlat: z.string().optional(),
        dateLivraison: z.string().optional(),
        photoUrl: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    // Chantier ambassadeurs (ADR 0008) : « remis par » après la remise. Le
    // ticket de remise ne vit plus sur la commande (un par personne et par
    // jour, GET /orders/ticket-du-jour → ticketJourSchema).
    deliveredBy: z.object({ prenom: z.string() }).nullable().optional(),
  })
  .passthrough();

// ── Votes — GET /votes/current ──
export const voteCandidateSchema = z
  .object({
    id: z.string(),
    nomPlat: z.string(),
    description: z.string().nullable().optional(),
    photoUrl: z.string().nullable().optional(),
    semaineNumero: z.number().optional(),
    annee: z.number().optional(),
    totalVotes: z.number().optional(),
    elu: z.boolean().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();

export const voteStateSchema = z
  .object({
    window: z
      .object({
        isOpen: z.boolean(),
        opensAt: z.string(),
        closesAt: z.string(),
      })
      .optional(),
    semaineNumero: z.number().optional(),
    annee: z.number().optional(),
    candidates: z.array(voteCandidateSchema).optional(),
    myVotes: z.array(z.string()).optional(),
    votesRestants: z.number().optional(),
    maxVotes: z.number().optional(),
  })
  .passthrough();

// ── Admin — GET /admin/alerts ──
export const adminAlertSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    typeAlerte: z.enum([
      'INACTIVE_7_DAYS',
      'CREDITS_EXPIRED_NO_RENEWAL',
      'ORDER_RATE_DROP',
      'NEVER_VOTED',
      'LOW_ENGAGEMENT_SCORE',
      'SUR_PAIEMENT',
      'DEMANDE_RECUPERATION',
      'REUTILISATION_SESSION',
    ]),
    message: z.string(),
    priorite: z.enum(['HIGH', 'MEDIUM', 'LOW']),
    resolue: z.boolean().optional(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable().optional(),
    user: z
      .object({
        id: z.string().optional(),
        nom: z.string().optional(),
        prenom: z.string().optional(),
        email: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

// ── Admin — GET /admin/clients (liste, forme déjà aplatie par le contrôleur) ──
export const adminClientSchema = z
  .object({
    id: z.string(),
    nom: z.string().optional(),
    prenom: z.string().optional(),
    email: z.string().nullable().optional(),
    telephone: z.string().optional(),
    statut: z.string().optional(),
    profil: z.string().optional(),
    canalAcquisition: z.string().nullable().optional(),
    formuleActive: z.string().nullable().optional(),
    creditsRestants: z.number().optional(),
    scoreEngagement: z.number().nullable().optional(),
    tauxCommande: z.number().nullable().optional(),
    niveauRisque: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).nullable().optional(),
    jourDepuisDerniereCmd: z.number().nullable().optional(),
    ltvTotal: z.number().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();

// ── Admin — GET /admin/clients/:id (objet User Prisma brut, sans passwordHash
// ni pushSubscription — cf. admin.controller.ts getClientDetail) : forme
// nichée, DIFFÉRENTE de la liste ci-dessus. Réutilise les schémas déjà
// partagés pour les relations incluses (D1 : pas de re-description d'une
// forme déjà décrite ailleurs). ──
export const adminClientDetailSchema = z
  .object({
    id: z.string(),
    nom: z.string(),
    prenom: z.string(),
    email: z.string().nullable(),
    telephone: z.string(),
    statut: z.string().optional(),
    profil: z.string().optional(),
    canalAcquisition: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    clientScore: z
      .object({
        scoreEngagement: z.number().nullable().optional(),
        tauxCommande: z.number().nullable().optional(),
        niveauRisque: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).nullable().optional(),
        jourDepuisDerniereCmd: z.number().nullable().optional(),
        delaiMoyenCommandeMin: z.number().nullable().optional(),
        nbCyclesCompletes: z.number().nullable().optional(),
        ltvTotal: z.number().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    subscriptions: z.array(subscriptionSchema).optional(),
    orders: z.array(orderSchema).optional(),
    payments: z.array(paymentSchema).optional(),
    alerts: z.array(adminAlertSchema).optional(),
    ambassade: z.object({ id: z.string(), titulaire: z.string() }).nullable().optional(),
    ambassadeTitulaire: z.object({ id: z.string(), statut: z.enum(['ACTIVE', 'FERMEE']), codeInvitation: z.string() }).nullable().optional(),
  })
  .passthrough();

// ── Admin — GET /orders/today (récap admin, forme DIFFÉRENTE de /orders/me :
// `orders` inclut `user`) ──
export const orderTodayItemSchema = orderSchema.extend({
  user: z
    .object({
      id: z.string().optional(),
      nom: z.string().optional(),
      prenom: z.string().optional(),
      email: z.string().nullable().optional(),
      ambassadeId: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export const ordersTodaySchema = z
  .object({
    orders: z.array(orderTodayItemSchema),
    stats: z
      .object({
        totalCommandes: z.number(),
        totalRepas: z.number(),
        membresUniques: z.number(),
        sansAmbassade: z.number().optional(),
      })
      .optional(),
  })
  .passthrough();

// ── Admin — GET /admin/metrics/pwa (tableau de bord baseline, ADR 0007 D2
// « Métriques A/B du cap natif ») — agrégats SEULEMENT (aucune donnée
// individuelle : pas de userId/téléphone/email dans cette forme) ──
export const pwaPlatformStatsSchema = z.object({
  count: z.number(),
  standalone: z.number(),
  optIn: z.number(),
});

export const pwaMetricsSchema = z
  .object({
    totalActifs: z.number(),
    // Dénominateur honnête (revue tâche 1) pour les taux standalone/opt-in
    // côté front : actifs ayant DÉJÀ envoyé au moins une télémétrie
    // (`pwaCollectedAt` non nul). Sans lui, les jamais-mesurés diluent les
    // taux vers le bas dès le lancement (comptés comme "non adoptés").
    mesuresCount: z.number(),
    standaloneCount: z.number(),
    optInCount: z.number(),
    parPlateforme: z.object({
      ios: pwaPlatformStatsSchema,
      android: pwaPlatformStatsSchema,
      other: pwaPlatformStatsSchema,
    }),
    // Cohorte passive (Métrique B, proxy) : actifs avec commande < 30j SANS
    // pushSubscription — hors périmètre score.service/alertes pour cette tâche.
    cohortePassive: z.number(),
  })
  .passthrough();

// ── Admin — GET/PUT /admin/settings(/deadline) (révision ADR 0004, chantier 2) ──
export const orderDeadlineRuleSchema = z.object({
  mode: z.enum(['VEILLE', 'JOUR_J']),
  heure: z.number().int().min(0).max(23),
});

export const adminSettingsSchema = z
  .object({
    orderDeadline: orderDeadlineRuleSchema,
    remunerationParRepas: z.number().nullable().optional(),
  })
  .passthrough();

// ── Ambassadeurs (ADR 0008) ──
export const ambassadeStatutSchema = z.enum(['ACTIVE', 'FERMEE']);

export const ambassadeMembreSchema = z.object({ id: z.string(), prenom: z.string(), nom: z.string() });

export const paieAmbassadeurSchema = z.object({
  semaine: z.string(),
  nbRepas: z.number(),
  montant: z.number().nullable(),
  historique: z.array(z.object({ semaine: z.string(), nbRepas: z.number(), montant: z.number(), payeAt: z.string() })),
});

export const ambassadeMeSchema = z.object({
  ambassade: z.object({
    id: z.string(), codeInvitation: z.string(), lienInvitation: z.string(), statut: ambassadeStatutSchema, absentLe: z.string().nullable(),
  }),
  membres: z.array(ambassadeMembreSchema),
  distributionAujourdhui: z.boolean(),
  // 11h–14h le jour J, décidé serveur : l'Accueil du titulaire bascule la
  // Distribution en CTA (artboard 5e « midi ») au lieu d'une carte sous la flottaison.
  distributionEnCours: z.boolean(),
  nbRepasAServirAujourdhui: z.number().int().nullable(),
  paie: paieAmbassadeurSchema.nullable(),
});

export const monAmbassadeurSchema = z.object({
  ambassade: z.object({ titulaire: z.string(), statut: ambassadeStatutSchema }).nullable(),
});

// Distribution (ADR 0008 révisé 2026-10-10) : une ligne par PERSONNE, ses
// commandes du jour agrégées ; un seul scan les sert toutes.
export const distributionPersonneSchema = z.object({
  userId: z.string(), prenom: z.string(), nom: z.string(), nomPlat: z.string(),
  nbCommandes: z.number().int(), nbRepas: z.number().int(),
  // Repas encore à tendre : un état partiel ne ment jamais sur la quantité.
  nbRepasAServir: z.number().int(),
  statut: z.enum(['A_SERVIR', 'SERVI']),
  livreeAt: z.string().nullable(), deliveredByPrenom: z.string().nullable(), membre: z.boolean(),
});

export const distributionSchema = z.object({
  jour: z.string(), ouverte: z.boolean(),
  personnes: z.array(distributionPersonneSchema),
  absences: z.array(z.object({ titulaire: z.string(), nbMembres: z.number() })),
});

export const remiseResultSchema = z.object({
  userId: z.string(), prenom: z.string(), nom: z.string(),
  nbCommandes: z.number().int(), nbRepas: z.number().int(), livreeAt: z.string().nullable(),
});

// GET /orders/ticket-du-jour : le ticket de remise d'une personne, un par
// jour, toutes ses commandes du jour ; `null` hors fenêtre ou sans commande.
export const ticketJourSchema = z.object({
  jour: z.string(), ticket: z.string(), code: z.string(),
  commandes: z.array(z.object({
    id: z.string(), nomPlat: z.string(), quantite: z.number().int(), statut: z.enum(['CONFIRMED', 'DELIVERED']),
    orderedAt: z.string(), livreeAt: z.string().nullable(), deliveredByPrenom: z.string().nullable(),
  })),
  nbCommandes: z.number().int(), nbRepas: z.number().int(),
  nbRepasAServir: z.number().int(),
  statut: z.enum(['A_SERVIR', 'REMIS']),
  livreeAt: z.string().nullable(), deliveredByPrenom: z.string().nullable(),
});

export const invitationSchema = z.object({ titulaire: z.string(), statut: ambassadeStatutSchema });

export const adminAmbassadeSchema = z.object({
  id: z.string(), statut: ambassadeStatutSchema, codeInvitation: z.string(), absentLe: z.string().nullable(),
  titulaire: z.object({ id: z.string(), prenom: z.string(), nom: z.string() }),
  nbMembres: z.number(), repasSemaine: z.number(),
});

export const paieLigneSchema = z.object({
  ambassadeurId: z.string(), prenom: z.string(), nom: z.string(), nbRepas: z.number(), montant: z.number().nullable(),
  recu: z.object({ nbRepas: z.number(), montant: z.number(), payeAt: z.string() }).nullable(),
});

export const paieSemaineSchema = z.object({ semaine: z.string(), bareme: z.number().nullable(), lignes: z.array(paieLigneSchema) });

// ── Types dérivés par z.infer — source unique de la forme (ADR 0005 D3) ──
export type AuthUserWire = z.infer<typeof authUserSchema>;
export type AuthResponseWire = z.infer<typeof authResponseSchema>;
export type RefreshResponseWire = z.infer<typeof refreshResponseSchema>;
export type SessionWire = z.infer<typeof sessionSchema>;
export type SessionsResponseWire = z.infer<typeof sessionsResponseSchema>;
export type RevokeAllSessionsResponseWire = z.infer<typeof revokeAllSessionsResponseSchema>;
export type MenuWire = z.infer<typeof menuSchema>;
export type FormuleInfoWire = z.infer<typeof formuleInfoSchema>;
export type SubscriptionWire = z.infer<typeof subscriptionSchema>;
export type PaymentWire = z.infer<typeof paymentSchema>;
export type OrderWire = z.infer<typeof orderSchema>;
export type VoteCandidateWire = z.infer<typeof voteCandidateSchema>;
export type VoteStateWire = z.infer<typeof voteStateSchema>;
export type AdminAlertWire = z.infer<typeof adminAlertSchema>;
export type AdminClientWire = z.infer<typeof adminClientSchema>;
export type AdminClientDetailWire = z.infer<typeof adminClientDetailSchema>;
export type OrderTodayItemWire = z.infer<typeof orderTodayItemSchema>;
export type OrdersTodayWire = z.infer<typeof ordersTodaySchema>;
export type OrderDeadlineRuleWire = z.infer<typeof orderDeadlineRuleSchema>;
export type AdminSettingsWire = z.infer<typeof adminSettingsSchema>;
export type PwaPlatformStatsWire = z.infer<typeof pwaPlatformStatsSchema>;
export type PwaMetricsWire = z.infer<typeof pwaMetricsSchema>;
export type AmbassadeMeWire = z.infer<typeof ambassadeMeSchema>;
export type MonAmbassadeurWire = z.infer<typeof monAmbassadeurSchema>;
export type DistributionWire = z.infer<typeof distributionSchema>;
export type DistributionPersonneWire = z.infer<typeof distributionPersonneSchema>;
export type TicketJourWire = z.infer<typeof ticketJourSchema>;
export type RemiseResultWire = z.infer<typeof remiseResultSchema>;
export type InvitationWire = z.infer<typeof invitationSchema>;
export type AdminAmbassadeWire = z.infer<typeof adminAmbassadeSchema>;
export type PaieSemaineWire = z.infer<typeof paieSemaineSchema>;
export type PaieLigneWire = z.infer<typeof paieLigneSchema>;
